import { Box, Button, Modal } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useEditClient } from "../service/ClientManagementService";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import {
  useGetCompanyCategories,
  useGetCompanyDetails,
  useGetCompanyIndustries,
} from "services/company";
import dayjs from "dayjs";

const DeleteRecipientModal = ({
  modalProps = {},
  id,
  onClose,
  recipientId,
}) => {
  const { setMessage } = useSnackbar();

  const { response: industriesResp } = useGetCompanyIndustries();
  const { response: categoriesResp } = useGetCompanyCategories();
  const { response: companyDetail, loading: loadingDetail } =
    useGetCompanyDetails({ id });
  const { mutate: updateClient, loading: loadingUpdate } = useEditClient({
    id,
  });
  const industryOptions = useMemo(
    () =>
      industriesResp?.data?.map((el) => ({
        label: el?.name,
        value: Number(el?.id),
      })),
    [industriesResp],
  );

  const categoryOptions = useMemo(
    () =>
      categoriesResp?.data?.map((el) => ({
        label: el?.name,
        value: Number(el?.id),
      })),
    [categoriesResp],
  );

  const handleDelete = useCallback(async () => {
    const clientDetail = companyDetail?.data || {};
    const payload = {
      name: clientDetail?.name,
      effect_code: clientDetail?.effect_code,
      subscription_type: clientDetail?.subscription_type,
      email_recipients: clientDetail?.email_recipients,
      year_established: Number(
        clientDetail?.year_established || dayjs(Date.now())?.format("YYYY"),
      ),
      industry: industryOptions?.find(
        (el) => el?.label === clientDetail?.industry_name,
      )?.value,
      category: categoryOptions?.find(
        (el) => el?.label === clientDetail?.category_name,
      )?.value,
      status: clientDetail?.status,
    };

    const emailRecipients = [...(clientDetail?.email_recipients || [])];
    emailRecipients[recipientId] = undefined;

    payload.email_recipients = emailRecipients?.filter((el) => Boolean(el));

    const response = await updateClient(payload);
    if (
      response?.info?.description === "Company is updated successfully" ||
      response?.info?.title === "success"
    ) {
      setMessage({
        desc: "Successfully deleted recipient.",
        type: "success",
      });
      onClose();
    } else {
      setMessage({
        desc: "Failed to delete recipient. Please try again.",
        type: "error",
      });
    }
  }, [
    categoryOptions,
    companyDetail?.data,
    industryOptions,
    onClose,
    recipientId,
    setMessage,
    updateClient,
  ]);

  return (
    <Modal {...modalProps}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 560,
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          px: "40px",
          py: "32px",
        }}
      >
        <div className='font-semibold mb-5 text-center'>
          Are you sure you want to delete this email recipient?
        </div>
        <div className='font-semibold mb-5 text-[#5F737F] text-center'>
          This action will permanently remove it from the system
        </div>
        <div className='flex flex-row gap-4 justify-center'>
          <Button variant='outlined' onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={loadingDetail || loadingUpdate}
            className='!bg-[#556FB5]'
            onClick={handleDelete}
            variant='contained'
          >
            Yes
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default DeleteRecipientModal;
